<template>
  <Dialog
    :open="open"
    @update:open="$emit('update:open', $event)"
    size="carousel"
    background="white"
    :title="title"
    :description="description"
    :showFooter="false"
  >
    <slot />
    <template #content>
      <div class="h-5 border-b border-secondary" />
      <div
        class="relative w-full h-full flex flex-col items-center justify-center grow md:p-4 lg:p-6 overflow-hidden"
      >
        <Transition
          :name="!forward ? 'slide-right' : 'slide-left'"
          mode="out-in"
        >
          <div
            :key="src"
            class="w-full h-full flex flex-col items-center justify-center overflow-hidden"
          >
            <Media
              v-show="mediaLoaded"
              :src="src"
              :type="type"
              controls
              preload="auto"
              class="md:rounded-sm overflow-hidden object-scale-down"
              @loaded="mediaLoaded = true"
            />
            <LoadingSpinner v-show="!mediaLoaded" size="xl" />
          </div>
        </Transition>
      </div>
      <div v-if="mediaLoaded && multiple">
        <CarouselArrow
          direction="left"
          class="absolute top-1/2 left-0 translate-y-1/2 mx-4 md:mx-8 lg:mx-10"
          @click="previous"
        />
        <CarouselArrow
          direction="right"
          class="absolute top-1/2 right-0 translate-y-1/2 mx-4 md:mx-8 lg:mx-10"
          @click="next"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { onUnmounted, ref, watch } from "vue";
import { onKeyStroke } from "@vueuse/core";
import { Dialog } from "@/SharedComponents/Dialog";
import { LoadingSpinner } from "@/SharedComponents/LoadingSpinner";
import { CarouselArrow, Media } from "@/SharedComponents/Media";

const emit = defineEmits(["update:open", "next", "previous"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  title: { type: String, required: true },
  description: { type: String, required: false },
  src: { type: String, required: false },
  type: { type: String, required: false },
  multiple: { type: Boolean, default: false },
});

const forward = ref(true);
const mediaLoaded = ref(false);

watch(
  () => [props.open, props.url],
  () => {
    if (props.open) {
      mediaLoaded.value = false;
    }
  }
);

const next = () => {
  forward.value = true;
  emit("next");
};

const previous = () => {
  forward.value = false;
  emit("previous");
};

const keyStrokeLeftEvent = onKeyStroke(["ArrowLeft"], () => {
  if (props.open) {
    previous();
  }
});

const keyStrokeRightEvent = onKeyStroke(["ArrowRight"], () => {
  if (props.open) {
    next();
  }
});

onUnmounted(() => {
  keyStrokeLeftEvent();
  keyStrokeRightEvent();
});
</script>

<style lang="scss" scoped>
.slide-right-enter-active {
  transition: all 0.2s ease-out;
}

.slide-right-leave-active {
  transition: all 0.1s cubic-bezier(0.55, 0, 0.1, 1);
}

.slide-right-enter-from {
  transform: translateX(-100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

.slide-left-enter-active {
  transition: all 0.2s ease-out;
}

.slide-left-leave-active {
  transition: all 0.1s cubic-bezier(0.55, 0, 0.1, 1);
}

.slide-left-enter-from {
  transform: translateX(100%);
}

.slide-left-leave-to {
  transform: translateX(-100%);
}
</style>
